import React, { useMemo, useState } from 'react';
import { User, UserGoal, UserRatingEnum, UserStatusEnum } from 'types';
import { GridColDef } from '@mui/x-data-grid';
import Section from 'components/Section';
import { Button, CircularProgress, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import DataGridTable from '.';
import { useUser } from 'contexts/userContext';
import { useNavigate } from 'react-router-dom';
import { useMutation, useLazyQuery } from '@apollo/client';
import {
  APPROVE_END_YEAR_REVIEWS,
  ApproveEndYearReviewsResponse,
  ApproveEndYearReviewsVariables,
  DOWNLOAD_END_YEAR_USER_REPORT,
  DownloadEndYearEvaluationReportResponse,
  DownloadEndYearEvaluationReportVariables,
  REJECT_END_YEAR_REVIEW,
  RejectENdYearReviewsResponse,
  RejectEndYearReviewsVariables,
} from 'graphql/mutation/userGoals';
import { toast } from 'react-toastify';
import { downloadBinaryData } from 'utils/admin';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { LoadingButton } from '@mui/lab';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { ConfirmationPopup, SimplePopup } from 'components/PopUp';
import TextField from 'components/Inputs/TextField';
import { getEnumKeyFromValue, getSelectedObjectiveGoalsYear } from 'utils/common';

type RowType = {
  _id: string;
  kra: string;
  kpi: string;
  weightage: number | string;
  target: string;
  user?: User;
  managerCommentEndYear?: string;
  managerRatingEndYear?: string;
};

const emptyRow = {
  _id: '',
  kra: '',
  kpi: '',
  weightage: '',
  target: '',
  managerCommentEndYear: '',
  managerRatingEndYear: '',
};

const emptyRows = [emptyRow, emptyRow, emptyRow, emptyRow, emptyRow];

const Evaluation3Table: React.FC<{
  initialRows?: UserGoal[];
  employee: User;
  title: string;
}> = ({ initialRows = emptyRows, employee, title }) => {
  const [rows, setRows] = useState<RowType[]>(
    initialRows.map((r, idx: number) => ({
      ...r,
      _id: !!r._id ? r._id : `new_${idx}`,
    }))
  );

  const [enableApprovePopup, toggleApprovalPopup] = useState(false);
  const [enableRejectPopup, toggleRejectPopup] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState('');

  const { user: loggedInUser } = useUser();

  const navigate = useNavigate();

  const isManager = loggedInUser?._id === employee.reportingManager?._id;

  const currentYear = new Date().getFullYear();
  const selectedYear = getSelectedObjectiveGoalsYear();

  const isRunningYear = currentYear === selectedYear;

  const [approvedUserGoals, { loading: approvingUserGoals }] = useMutation<
    ApproveEndYearReviewsResponse,
    ApproveEndYearReviewsVariables
  >(APPROVE_END_YEAR_REVIEWS);

  const [downloadEndYearUserReport, { loading: downloadingEndYearUserReport }] = useLazyQuery<
    DownloadEndYearEvaluationReportResponse,
    DownloadEndYearEvaluationReportVariables
  >(DOWNLOAD_END_YEAR_USER_REPORT, {
    variables: {
      year: getSelectedObjectiveGoalsYear(),
      users: [employee._id],
    },
    fetchPolicy: 'network-only',
  });

  const [rejectUserGoals, { loading: rejectingUserGoals }] = useMutation<
    RejectENdYearReviewsResponse,
    RejectEndYearReviewsVariables
  >(REJECT_END_YEAR_REVIEW);

  const renderMultilineText = (value: string) => {
    return (
      <Grid maxHeight="60px" my="auto" overflow="auto" display="flex" flexDirection="column">
        {value.split('\n').map((item, index) => (
          <Grid item key={index}>
            {item}
          </Grid>
        ))}
      </Grid>
    );
  };

  const isMangerInputFilled = useMemo(() => {
    for (let i = 0; i < rows.length; i++) {
      if (!!!rows[i].managerCommentEndYear || !!!rows[i].managerRatingEndYear) {
        return false;
      }
    }
    return true;
  }, [rows]);

  const columns: GridColDef[] = [
    {
      field: 'serialNumber',
      headerName: 'S.No',
      width: 50,
      valueGetter: (_, row, __, api) => api.current.getRowIndexRelativeToVisibleRows(row._id) + 1,
    },
    {
      field: 'kra',
      headerName: 'Key Result Areas',
      width: 130,
      renderCell: params => renderMultilineText(params.value ?? ''),
    },
    {
      field: 'kpi',
      headerName: 'Key Performance Indicators',
      width: 200,
      renderCell: params => renderMultilineText(params.value ?? ''),
    },

    {
      field: 'weightage',
      headerName: 'Weightage (%)',
      width: 110,
      valueFormatter: val => (!!!val ? '0' : val),
    },
    {
      field: 'empCommentEndYear',
      headerName: 'Employee Review',
      flex: 1,
      width: 250,
      renderCell: params => renderMultilineText(params.value ?? ''),
    },
    {
      field: 'empRatingEndYear',
      headerName: 'Employee Rating',
      flex: 1,
      width: 250,
      renderCell: params =>
        renderMultilineText(getEnumKeyFromValue(UserRatingEnum, params.value) ?? ''),
    },
    {
      field: 'managerCommentEndYear',
      headerName: 'Manager Review',
      width: 250,
      flex: 1,
      renderCell: params => renderMultilineText(params.value ?? ''),
    },
    {
      field: 'managerRatingEndYear',
      headerName: 'Manager Rating',
      width: 250,
      flex: 1,
      renderCell: params =>
        renderMultilineText(getEnumKeyFromValue(UserRatingEnum, params.value) ?? ''),
    },
  ];

  const statusMsg = useMemo(() => {
    switch (employee?.annualUserInfo?.endYearStatus) {
      case UserStatusEnum['NOT STARTED']:
      case UserStatusEnum['DRAFT']:
        return (
          <Typography color={'red'}>
            <b>
              {employee.name} has <i>NOT SUBMITTED</i> their end year review.
            </b>
          </Typography>
        );
      case UserStatusEnum['PENDING FOR APPROVAL']:
        return (
          <Typography color={'blue'}>
            <b>
              {' '}
              {employee.name} has <i>SUBMITTED</i> their end year review
            </b>
          </Typography>
        );
      case UserStatusEnum.APPROVED:
        return (
          <Typography color={'green'}>
            <b>
              {employee.name}'s end year review are <i>Approved</i>
            </b>
          </Typography>
        );
      case UserStatusEnum.FREEZE:
        return (
          <Typography color={'blueviolet'}>
            <b>
              {employee.name}'s end year review are <i>Freezed</i>
            </b>
          </Typography>
        );
      default:
        return '';
    }
  }, [employee?.annualUserInfo?.endYearStatus]);

  const handleApproveUserGoals = () => {
    approvedUserGoals({
      variables: {
        userId: employee._id,
      },
      onCompleted: () => {
        toast.success('Approved Successfully');
        toggleApprovalPopup(false);
      },
    });
  };

  const handleRejectUserGoals = () => {
    rejectUserGoals({
      variables: { userId: employee._id, remarks: rejectRemarks },
      onCompleted: () => {
        toast.success('Rejected Successfully');
        toggleRejectPopup(false);
        setRows(initialRows);
        setRejectRemarks('');
      },
    });
  };

  const handleReportDownload = () => {
    downloadEndYearUserReport({
      onCompleted: res =>
        downloadBinaryData({
          content: res.downloadEndYearEvaluationReports.file,
          filename: res.downloadEndYearEvaluationReports.fileName,
        }),
    });
  };
  return (
    <Section
      title={title}
      collapsible
      action={
        <Tooltip title="Download Objectives">
          <IconButton sx={{ color: 'white' }}>
            {downloadingEndYearUserReport ? (
              <CircularProgress size={21} color="inherit" />
            ) : (
              <SummarizeIcon color="inherit" onClick={handleReportDownload} />
            )}
          </IconButton>
        </Tooltip>
      }
    >
      <Grid container direction="column">
        <DataGridTable
          rows={rows}
          columns={columns}
          getRowId={row => row._id}
          hideFooter
          sx={{
            maxWidth: '78vw',
            mx: 'auto',
            minHeight: '10vh',
          }}
        />
        <Grid item mt={2} xs={12} container justifyContent="space-between">
          <Grid item xs={6} letterSpacing={1.1} pl={3}>
            {statusMsg}
          </Grid>
          <Grid
            container
            item
            xs={6}
            justifyContent={'flex-end'}
            columnGap={1.5}
            alignItems={'center'}
          >
            {isManager &&
              ((isRunningYear && employee.annualUserInfo?.period > 2) ||
                (currentYear - selectedYear === 1 && employee?.annualUserInfo?.period > 2)) && (
                <>
                  <LoadingButton
                    variant="contained"
                    color="success"
                    onClick={() => toggleApprovalPopup(true)}
                    disabled={
                      employee?.annualUserInfo?.endYearStatus !==
                        UserStatusEnum['PENDING FOR APPROVAL'] || !isMangerInputFilled
                    }
                  >
                    Approve
                  </LoadingButton>
                  <LoadingButton
                    variant="contained"
                    color="error"
                    onClick={() => toggleRejectPopup(true)}
                    disabled={
                      employee?.annualUserInfo?.endYearStatus !==
                        UserStatusEnum['PENDING FOR APPROVAL'] || !isMangerInputFilled
                    }
                  >
                    Reject
                  </LoadingButton>
                </>
              )}
            <Button
              startIcon={<EditNoteIcon />}
              variant="contained"
              onClick={() => navigate('edit?period=3')}
            >
              View
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <ConfirmationPopup
        fullWidth
        key="approve-popup"
        onClose={() => toggleApprovalPopup(false)}
        open={enableApprovePopup}
        title="Do you want to approve end year reviews?"
        onConfirmation={handleApproveUserGoals}
        loading={approvingUserGoals}
      />
      <SimplePopup
        fullWidth
        key="reject-popup"
        onClose={() => toggleRejectPopup(false)}
        open={enableRejectPopup}
        title="Do you want to reject end year reviews?"
      >
        <form
          onSubmit={e => {
            e.preventDefault();
            handleRejectUserGoals();
          }}
        >
          <Grid container px={1.5} py={1.5} rowGap={2} columnSpacing={1.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                required
                value={rejectRemarks}
                onChange={e => setRejectRemarks(e.target.value)}
              />
            </Grid>
            <Grid container item xs={12} alignItems="center" columnSpacing={1.5}>
              <Grid item xs={6}>
                <LoadingButton
                  fullWidth
                  type="submit"
                  loading={rejectingUserGoals}
                  variant="contained"
                >
                  Yes
                </LoadingButton>
              </Grid>
              <Grid item xs={6}>
                <Button variant="outlined" fullWidth onClick={() => toggleRejectPopup(false)}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </SimplePopup>
    </Section>
  );
};

export default Evaluation3Table;
